import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  firstUnansweredIndex,
  SURVEY_QUESTIONS,
  SurveyAnswerKey,
  SurveyAnswers,
  SurveyAnswerValue,
  SurveyMode,
  SurveyTrigger,
} from "../../common/postTrialSurvey";
import { useAuth } from "../../context/AuthContext";
import { surveyApi, SurveyEligibility } from "./api";

// Same delay the tutorials use, so the modal never pops before the page
// has settled.
const OPEN_DELAY_MS = 800;
const AUTOSAVE_DEBOUNCE_MS = 500;

// One eligibility fetch per page load, shared by every mount point
// (Dashboard and the paywall page both render the provider) and immune to
// StrictMode's double effects.
let eligibilityPromise: Promise<SurveyEligibility> | null = null;
function fetchEligibilityOnce(): Promise<SurveyEligibility> {
  if (!eligibilityPromise) {
    eligibilityPromise = surveyApi.getEligibility().catch((err) => {
      eligibilityPromise = null;
      throw err;
    });
  }
  return eligibilityPromise;
}

export type PostTrialSurveyState = {
  resolved: boolean;
  open: boolean;
  trigger: SurveyTrigger | null;
  mode: SurveyMode;
  agencyName: string | null;
  endsAt: Date | null;
  // "I'm still evaluating" target; null hides the button.
  postponeUntil: Date | null;
  index: number;
  answers: SurveyAnswers;
  quote: string;
  saving: boolean;
  submitting: boolean;
  setAnswer: (id: SurveyAnswerKey, value: SurveyAnswerValue | null) => void;
  setQuote: (text: string) => void;
  next: () => void;
  back: () => void;
  goTo: (index: number) => void;
  complete: () => Promise<void>;
  postpone: () => Promise<Date>;
  postponing: boolean;
};

export function usePostTrialSurvey(): PostTrialSurveyState {
  const { isAuthenticated, loading } = useAuth();
  const [resolved, setResolved] = useState(false);
  const [open, setOpen] = useState(false);
  const [trigger, setTrigger] = useState<SurveyTrigger | null>(null);
  const [mode, setMode] = useState<SurveyMode>("trial");
  const [agencyName, setAgencyName] = useState<string | null>(null);
  const [endsAt, setEndsAt] = useState<Date | null>(null);
  const [postponeUntil, setPostponeUntil] = useState<Date | null>(null);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<SurveyAnswers>({});
  const [quote, setQuoteState] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [postponing, setPostponing] = useState(false);

  // Pending partial write, flushed on a timer or before Next/Finish.
  const pendingRef = useRef<{ answers: SurveyAnswers; quote?: string } | null>(null);
  const timerRef = useRef<number | null>(null);
  const latestRef = useRef({ answers, quote, trigger });
  latestRef.current = { answers, quote, trigger };

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    let cancelled = false;
    let timer: number | null = null;
    fetchEligibilityOnce()
      .then((e) => {
        if (cancelled) return;
        setTrigger(e.trigger);
        setMode(e.mode ?? "trial");
        setAgencyName(e.agencyName ?? null);
        setEndsAt(e.endsAt ? new Date(e.endsAt) : null);
        setPostponeUntil(e.postponeUntil ? new Date(e.postponeUntil) : null);
        if (e.existing) {
          setAnswers(e.existing.answers ?? {});
          setQuoteState(e.existing.quote ?? "");
          setIndex(firstUnansweredIndex(e.existing.answers ?? {}, e.existing.quote));
        }
        if (e.eligible && e.trigger) {
          timer = window.setTimeout(() => !cancelled && setOpen(true), OPEN_DELAY_MS);
        }
      })
      .catch((err) => {
        // A 403 here means an agency/admin session — never an officer.
        if (err?.response?.status !== 403) console.error("Survey eligibility failed:", err);
      })
      .finally(() => !cancelled && setResolved(true));
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [isAuthenticated, loading]);

  const flush = useCallback(async () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    const t = latestRef.current.trigger;
    if (!pending || !t) return;
    pendingRef.current = null;
    setSaving(true);
    try {
      await surveyApi.save({
        answers: pending.answers,
        ...(pending.quote !== undefined && { quote: pending.quote }),
        platform: "web",
        trigger: t,
      });
    } catch (err) {
      // Autosave is best-effort; the final submit sends everything again.
      console.error("Survey autosave failed:", err);
    } finally {
      setSaving(false);
    }
  }, []);

  const queue = useCallback(
    (patch: { answers?: SurveyAnswers; quote?: string }) => {
      const current = pendingRef.current ?? { answers: {} };
      pendingRef.current = {
        answers: { ...current.answers, ...(patch.answers ?? {}) },
        quote: patch.quote ?? current.quote,
      };
      if (timerRef.current) window.clearTimeout(timerRef.current);
      timerRef.current = window.setTimeout(() => void flush(), AUTOSAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  const setAnswer = useCallback(
    (id: SurveyAnswerKey, value: SurveyAnswerValue | null) => {
      setAnswers((prev) => {
        const next: Record<string, SurveyAnswerValue> = { ...prev };
        if (value === null) delete next[id];
        else next[id] = value;
        return next as SurveyAnswers;
      });
      // null travels to the server as an explicit clear.
      queue({ answers: { [id]: value } as SurveyAnswers });
    },
    [queue]
  );

  const setQuote = useCallback(
    (text: string) => {
      setQuoteState(text);
      queue({ quote: text });
    },
    [queue]
  );

  const last = SURVEY_QUESTIONS.length - 1;
  const next = useCallback(() => {
    void flush();
    setIndex((i) => Math.min(i + 1, last));
  }, [flush, last]);
  const back = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);
  const goTo = useCallback((i: number) => setIndex(Math.max(0, Math.min(i, last))), [last]);

  const complete = useCallback(async () => {
    const t = latestRef.current.trigger;
    if (!t) return;
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    pendingRef.current = null;
    setSubmitting(true);
    try {
      const { answers: a, quote: q } = latestRef.current;
      await surveyApi.complete({
        answers: a,
        quote: q,
        platform: "web",
        trigger: t,
      });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  }, []);

  // Saves whatever is pending, then hides the survey until the date the
  // server returns.
  const postpone = useCallback(async () => {
    setPostponing(true);
    try {
      await flush();
      const { postponedUntil } = await surveyApi.postpone();
      setOpen(false);
      return new Date(postponedUntil);
    } finally {
      setPostponing(false);
    }
  }, [flush]);

  return useMemo(
    () => ({
      resolved,
      open,
      trigger,
      mode,
      agencyName,
      endsAt,
      postponeUntil,
      index,
      answers,
      quote,
      saving,
      submitting,
      setAnswer,
      setQuote,
      next,
      back,
      goTo,
      complete,
      postpone,
      postponing,
    }),
    [resolved, open, trigger, mode, agencyName, endsAt, postponeUntil, index, answers, quote, saving, submitting, setAnswer, setQuote, next, back, goTo, complete, postpone, postponing]
  );
}
